import { breachedPasswordMessage, checkPasswordBreached } from '@auctisync/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { sha1Hex } from '../../lib/sha1';
import { supabase } from '../../lib/supabase';
import { destinationForClaims, readClaims, useSession } from '../../hooks/useSession';

// 'checking' is the breach lookup, which happens BEFORE anything is saved.
// It gets its own label so a slow HaveIBeenPwned response can't leave the user
// reading "Saving…" for seconds and believing the password was already changed.
type Mode = 'idle' | 'checking' | 'submitting' | 'done';

/**
 * Reached after a user clicks a password-reset email link. The Supabase
 * client has already exchanged the hash for a recovery session via
 * AuthCallback, so we just collect a new password and call updateUser().
 *
 * After success, we route based on the user's primary role:
 *   super_admin → /super/dashboard
 *   anyone else → /admin/dashboard
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const { session, loading, userId } = useSession();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);

  // If someone lands here without a session, bounce them to the bidder
  // sign-in. This page is shared with staff recovery, but app.auctisync.com
  // roots at the bidder surface and staff sign in via their own /admin — so
  // /signin is the safer default for the shared app.
  //
  // BUT arriving straight from a password-recovery email link, the session is
  // not available on the very first `getSession()` — the Supabase client
  // consumes the URL hash asynchronously (`detectSessionInUrl`) and delivers
  // the recovery session a beat later via `onAuthStateChange`. Bouncing the
  // instant `getSession()` resolves null therefore kicks a VALID recovery link
  // straight to /signin (it flashes this page then bounces). Give the session
  // a short window to arrive before giving up — the timeout is cancelled the
  // moment a session lands (this effect re-runs with `userId` set). Mirrors
  // AuthCallback's session poll.
  useEffect(() => {
    if (loading || userId !== null) return;
    const bounce = setTimeout(() => {
      void navigate('/signin', { replace: true });
    }, 4000);
    return () => clearTimeout(bounce);
  }, [loading, userId, navigate]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    // Same breach check as sign-up. Applies to BOTH paths that reach this form:
    // recovery from a reset email, and the in-app "Change password" links. A
    // user must not be able to recover their way INTO a known-breached password.
    setMode('checking');
    const breach = await checkPasswordBreached(password, sha1Hex);
    if (breach.breached) {
      setError(breachedPasswordMessage(breach.count));
      setMode('idle');
      return;
    }

    setMode('submitting');
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setMode('idle');
      return;
    }

    setMode('done');
    // Bidders have no role claim, so destinationForClaims would bounce them to
    // /unauthorized — route them into the bidder home (`/`) instead. Staff/super
    // keep their role-based destination.
    const claims = readClaims(session);
    const destination =
      !claims.isSuperAdmin && claims.appRole === null ? '/' : destinationForClaims(claims);
    await navigate(destination, { replace: true });
  }

  const busy = mode === 'checking' || mode === 'submitting';
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: '1px solid var(--border, #e5e7eb)',
    background: 'var(--surface, #ffffff)',
    color: 'var(--text, #111)',
    fontFamily: 'var(--font-body, system-ui)',
    fontSize: '0.875rem',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <Card style={{ width: '100%', maxWidth: '28rem' }}>
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>
            Minimum 8 characters. You&rsquo;ll be signed in automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            style={{ display: 'grid', gap: '0.75rem' }}
          >
            <div>
              <label htmlFor="reset-password" style={labelStyle}>
                New password
              </label>
              <input
                id="reset-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                disabled={busy}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="reset-confirm" style={labelStyle}>
                Confirm password
              </label>
              <input
                id="reset-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                disabled={busy}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={inputStyle}
              />
            </div>

            {error && (
              <div
                role="alert"
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--semantic-error, #b91c1c)',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-sm, 0.375rem)',
                  background: 'var(--semantic-error-bg, #fef2f2)',
                  border: '1px solid var(--semantic-error, #b91c1c)',
                }}
              >
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy}>
              {mode === 'checking' ? 'Checking password…' : busy ? 'Saving…' : 'Set new password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
